import yargs from "yargs";
import { hideBin } from "yargs/helpers";
import chokidar from "chokidar";
import * as chalk from "chalk";

const chalkAny: any = (chalk as any).default ?? chalk;
import type { MockAPIServer, MockServerOptions } from "./types";
import { parseSpec } from "./parser";
import { createMockServer } from "./server";
import { loadConfig, normalizeConfig } from "./config";

async function startCommand(
  specFile: string,
  opts: {
    port?: number;
    delay?: number;
    watch?: boolean;
    errors?: boolean;
    slow?: boolean;
    verbose?: boolean;
  }
) {
  let server: MockAPIServer | null = null;
  const port = opts.port ?? 3000;
  const delay = opts.delay ?? 0;

  const userConfig = await loadConfig();
  const runtimeConfig = normalizeConfig(userConfig);

  const serverOptions: MockServerOptions = {
    port,
    delay,
    generators: runtimeConfig.generators,
    delayRange: opts.slow
      ? runtimeConfig.delayRange ?? { min: 2000, max: 5000 }
      : runtimeConfig.delayRange,
    errorRate: opts.errors
      ? runtimeConfig.errorRate ?? 0.1
      : runtimeConfig.errorRate,
  };

  async function boot() {
    try {
      if (opts.verbose) {
        console.log(chalkAny.dim(`Parsing OpenAPI spec: ${specFile}`));
      }
      const parsed = await parseSpec(specFile);

      if (opts.verbose) {
        console.log(chalkAny.dim(`Found ${parsed.routes.length} route(s)`));
      }

      server = createMockServer(parsed, {
        ...serverOptions,
        verbose: opts.verbose,
      });
      await server.start();

      // Print banner
      console.log();
      console.log(
        chalkAny.bold.cyan("╔════════════════════════════════════════╗")
      );
      console.log(
        chalkAny.bold.cyan("║   api-mock-smart Mock API Server      ║")
      );
      console.log(
        chalkAny.bold.cyan("╚════════════════════════════════════════╝")
      );
      console.log();

      // Print server info
      const delayInfo = serverOptions.delayRange
        ? `variable (${serverOptions.delayRange.min}-${serverOptions.delayRange.max}ms)`
        : `${delay}ms`;
      console.log(
        chalkAny.green("✓ Server running on"),
        chalkAny.bold(`http://localhost:${port}`)
      );
      console.log(chalkAny.dim(`  Delay: ${delayInfo}`));
      if (serverOptions.errorRate && serverOptions.errorRate > 0) {
        console.log(
          chalkAny.dim(
            `  Error rate: ${(serverOptions.errorRate * 100).toFixed(1)}%`
          )
        );
      }
      console.log();

      // Print routes table
      console.log(chalkAny.bold("Available Routes:"));
      console.log(chalkAny.dim("─".repeat(60)));
      for (const route of parsed.routes) {
        const methodColor = methodToColor(route.method);
        const methodStr = route.method.toUpperCase().padEnd(6);
        const pathStr = route.path.padEnd(30);
        const summaryStr = route.summary
          ? chalkAny.dim(`- ${route.summary}`)
          : "";
        console.log(
          `  ${methodColor(methodStr)} ${chalkAny.cyan(pathStr)} ${summaryStr}`
        );
      }
      console.log(chalkAny.dim("─".repeat(60)));
      console.log();

      if (opts.verbose) {
        console.log(
          chalkAny.dim("Verbose mode: Request/response logging enabled")
        );
        console.log();
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      console.error(chalkAny.red("✗ Failed to start mock server:"));
      console.error(chalkAny.red(`  ${message}`));
      if (opts.verbose && error instanceof Error && error.stack) {
        console.error(chalkAny.dim(error.stack));
      }
      process.exit(1);
    }
  }

  async function shutdown() {
    if (server) {
      try {
        await server.stop();
      } catch {
        // ignore
      }
      server = null;
    }
  }

  await boot();

  if (opts.watch) {
    console.log(chalkAny.yellow(`Watching spec file for changes: ${specFile}`));
    const watcher = chokidar.watch(specFile, { ignoreInitial: true });
    watcher.on("change", async () => {
      console.log(chalkAny.yellow("Spec file changed, reloading..."));
      await shutdown();
      await boot();
    });
  }
}

function methodToColor(method: string): (str: string) => string {
  switch (method.toLowerCase()) {
    case "get":
      return chalkAny.green;
    case "post":
      return chalkAny.blue;
    case "put":
      return chalkAny.yellow;
    case "delete":
      return chalkAny.red;
    case "patch":
      return chalkAny.magenta;
    default:
      return chalkAny.white;
  }
}

export function runCli(argv = hideBin(process.argv)) {
  void yargs(argv)
    .scriptName("api-mock-smart")
    .command(
      "start <spec>",
      "Start mock API server from an OpenAPI/Swagger spec file",
      (y) =>
        y
          .positional("spec", {
            describe: "Path or URL to OpenAPI/Swagger spec (YAML or JSON)",
            type: "string",
          })
          .option("port", {
            alias: "p",
            type: "number",
            describe: "Port to run the server on",
            default: 3000,
          })
          .option("delay", {
            alias: "d",
            type: "number",
            describe: "Response delay in milliseconds",
            default: 0,
          })
          .option("watch", {
            alias: "w",
            type: "boolean",
            describe: "Watch the spec file and reload on change",
            default: false,
          })
          .option("errors", {
            type: "boolean",
            describe:
              "Randomly return error responses (10% probability by default)",
            default: false,
          })
          .option("slow", {
            type: "boolean",
            describe: "Simulate slow network with 2-5s response delays",
            default: false,
          })
          .option("verbose", {
            alias: "v",
            type: "boolean",
            describe: "Show detailed request/response logging",
            default: false,
          }),
      async (args) => {
        const specFile = args.spec as string;
        await startCommand(specFile, {
          port: args.port,
          delay: args.delay,
          watch: args.watch,
          errors: args.errors,
          slow: args.slow,
          verbose: args.verbose,
        });
      }
    )
    .demandCommand(1)
    .help()
    .strict()
    .parse();
}

// Only auto-run when executed as a script in CommonJS environments
if (typeof require !== "undefined" && require.main === module) {
  runCli();
}
